import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaCalendar, FaUser, FaTag, FaArrowLeft } from 'react-icons/fa';
import apiService from '../services/api';
import BlogCard from '../components/BlogCard';
import Loading from '../components/Loading';

const BlogDetailsPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogDetails();
  }, [slug]);

  const fetchBlogDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.getBlogDetails(slug);
      if (response.data.success) {
        setBlog(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching blog details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) return <Loading message="Loading article..." />;
  if (!blog) return <div className="container-custom py-20 text-center">Article not found</div>;

  return (
    <>
      <Helmet>
        <title>{blog.title} - EduConsult Blog</title>
        <meta name="description" content={blog.excerpt || blog.title} />
      </Helmet>

      {/* Hero */}
      <section className="relative h-96 overflow-hidden">
        {blog.featured_image ? (
          <img src={blog.featured_image} alt={blog.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-600 to-secondary-600"></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 right-0 pb-12">
          <div className="container-custom">
            <Link
              to="/blogs"
              className="inline-flex items-center text-white mb-4 hover:text-gray-200 transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Back to Blog
            </Link>
            
            <div className="max-w-4xl">
              {blog.category && (
                <span className="inline-block px-4 py-1 bg-primary-600 text-white rounded-full text-sm font-medium mb-4">
                  {blog.category}
                </span>
              )}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-4">
                {blog.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm">
                {blog.author && (
                  <div className="flex items-center">
                    <FaUser className="mr-2" />
                    {blog.author}
                  </div>
                )}
                <div className="flex items-center">
                  <FaCalendar className="mr-2" />
                  {formatDate(blog.published_at)}
                </div>
                {blog.views && (
                  <div>{blog.views} views</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="prose prose-lg max-w-none">
                {blog.excerpt && (
                  <p className="text-xl text-gray-600 font-medium leading-relaxed mb-8">
                    {blog.excerpt}
                  </p>
                )}
                
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {blog.content}
                </div>
              </article>

              {/* Tags */}
              {blog.tags && (
                <div className="mt-12 pt-8 border-t">
                  <div className="flex items-center flex-wrap gap-2">
                    <FaTag className="text-gray-400" />
                    {blog.tags.split(',').map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Related Posts */}
              {blog.related_blogs && blog.related_blogs.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {blog.related_blogs.map((relatedBlog) => (
                      <Link
                        key={relatedBlog.id}
                        to={`/blogs/${relatedBlog.slug}`}
                        className="block group"
                      >
                        <h4 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-1 line-clamp-2">
                          {relatedBlog.title}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {formatDate(relatedBlog.published_at)}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-3">Need Expert Guidance?</h3>
                <p className="text-white/90 text-sm mb-4">
                  Get personalized consultation from our experienced counselors
                </p>
                <Link
                  to="/register"
                  className="block text-center btn bg-white text-primary-600 hover:bg-gray-100"
                >
                  Book Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetailsPage;
